import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { Page, ResponseError } from '../type/type';
import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';

export interface IConsultOrderCode {
  id: number;
  trkId: string;
  campaignName: string;
  showFooter?: boolean;
  topImageUrls?: string[];
  bottomImageUrls?: string[];
  // 픽셀 ID
  facebookPixelId?: string;
  kakaoPixelId?: string;
  googleAdsPixelId?: string;
  googleAdsEventSnippet?: string;
  daangnPixelId?: string;
  tiktokPixelId?: string;
  // 버튼명
  consultButtonName?: string;
  // 탭 타이틀
  documentTitle?: string;
  companyName?: string;
  bizProfileName?: string;
  franchiseName?: string;
  landedUserCount: number;
  resultUserCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface IConsultOrderCodeCreateRequest {
  campaignName: string;
  showFooter?: boolean;
  topImageUrls?: string[];
  bottomImageUrls?: string[];
  // 픽셀 ID
  facebookPixelId?: string;
  kakaoPixelId?: string;
  googleAdsPixelId?: string;
  googleAdsEventSnippet?: string;
  daangnPixelId?: string;
  tiktokPixelId?: string;
  // 버튼명
  consultButtonName?: string;
  // 탭 타이틀
  documentTitle?: string;
}

export interface IConsultOrderCodeUpdateRequest {
  campaignName?: string;
  showFooter?: boolean;
  topImageUrls?: string[];
  bottomImageUrls?: string[];
  // 픽셀 ID
  facebookPixelId?: string;
  kakaoPixelId?: string;
  googleAdsPixelId?: string;
  googleAdsEventSnippet?: string;
  daangnPixelId?: string;
  tiktokPixelId?: string;
  // 버튼명
  consultButtonName?: string;
  // 탭 타이틀
  documentTitle?: string;
}

export interface IConsultOrderCodeSearch {
  campaignName?: string;
  trkId?: string;
  franchiseIds?: string[]; // ROLE_ADMIN 전용
}

interface ConsultOrderCodeResponse extends Page {
  content: IConsultOrderCode[];
}

type ConsultOrderCodeState = {
  codeList: IConsultOrderCode[];
  codePage: Page | null;
  loading: boolean;
  search: IConsultOrderCodeSearch;
};

type ConsultOrderCodeAction = {
  fetchCodes: (page?: number, size?: number) => Promise<void>;
  setSearch: (search: IConsultOrderCodeSearch) => void;
  createCode: (payload: IConsultOrderCodeCreateRequest) => Promise<void>;
  updateCode: (id: number, payload: IConsultOrderCodeUpdateRequest) => Promise<boolean>;
  deleteCodes: (ids: number[]) => Promise<boolean>;
  reset: () => void;
};

export const useConsultOrderCodeStore = create<
  ConsultOrderCodeState & ConsultOrderCodeAction
>((set, get) => ({
  codeList: [],
  codePage: null,
  loading: false,
  search: {},

  setSearch: (search: IConsultOrderCodeSearch) => {
    set({ search });
  },

  fetchCodes: async (page = 0, size = 20) => {
    set({ loading: true });
    try {
      const { search } = get();
      const response = await apiClient.get<ConsultOrderCodeResponse>(
        '/consult-order/tracking',
        {
          params: {
            campaignName: search.campaignName?.trim() || undefined,
            trkId: search.trkId?.trim() || undefined,
            franchiseIds: search.franchiseIds?.length ? search.franchiseIds.join(',') : undefined,
            page,
            size,
            sort: 'createdAt,desc',
          },
        }
      );
      const { content, ...rest } = response.data;
      set({ codeList: content, codePage: rest });
    } catch (error) {
      console.error(error);
      toast.error('트래킹 코드를 불러오지 못했습니다.');
    }
    set({ loading: false });
  },

  createCode: async (payload: IConsultOrderCodeCreateRequest) => {
    if (!payload.campaignName.trim()) {
      toast.error('캠페인명을 입력해주세요.');
      return;
    }
    try {
      await apiClient.post('/consult-order/tracking', payload);
      toast.success('새로운 트래킹 코드가 생성되었습니다.');
      const { codePage } = get();
      const pageNumber = codePage?.pageable?.pageNumber ?? 0;
      await get().fetchCodes(pageNumber);
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg ?? '코드 생성에 실패했습니다.');
      } else {
        console.error(error);
        toast.error('코드 생성에 실패했습니다.');
      }
    }
  },

  updateCode: async (id: number, payload: IConsultOrderCodeUpdateRequest) => {
    try {
      await apiClient.patch(`/consult-order/tracking/${id}`, payload);
      toast.success('캠페인 정보가 수정되었습니다.');
      return true;
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg ?? '캠페인 수정에 실패했습니다.');
      } else {
        console.error(error);
        toast.error('캠페인 수정에 실패했습니다.');
      }
      return false;
    }
  },

  deleteCodes: async (ids: number[]) => {
    if (!ids || ids.length === 0) {
      toast.warn('선택된 캠페인이 없습니다.');
      return false;
    }
    try {
      await apiClient.delete('/consult-order/tracking', { data: { ids } });
      toast.success(`${ids.length}건의 캠페인이 삭제되었습니다.`);
      const { codePage } = get();
      const pageNumber = codePage?.pageable?.pageNumber ?? 0;
      await get().fetchCodes(pageNumber);
      return true;
    } catch (error) {
      if (isAxiosError<ResponseError>(error)) {
        toast.error(error.response?.data.errorMsg ?? '캠페인 삭제에 실패했습니다.');
      } else {
        console.error(error);
        toast.error('캠페인 삭제에 실패했습니다.');
      }
      return false;
    }
  },

  reset: () => {
    set({ codeList: [], codePage: null, loading: false, search: {} });
  },
}));
